'use client'

import { useState } from 'react'
import clsx from 'clsx'

interface HsNoteLinksProps {
  partNote?: string
  partNumber: string
  chapterNote?: string
  chapterNumber?: string
}

export default function HsNoteLinks({
  partNote,
  partNumber,
  chapterNote,
  chapterNumber,
}: HsNoteLinksProps) {
  const [activeNote, setActiveNote] = useState<{
    title: string
    content: string
  } | null>(null)

  const handleNoteClick = (title: string, content?: string) => {
    if (content) {
      setActiveNote({ title, content })
    }
  }

  const closeModal = () => {
    setActiveNote(null)
  }

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-4">
        {partNote && (
          <NoteLink
            icon="book"
            label={`Xem chú giải ${partNumber}`}
            onClick={() =>
              handleNoteClick(`Chú giải ${partNumber}`, partNote)
            }
          />
        )}
        {chapterNote && chapterNumber && (
          <NoteLink
            icon="book-open"
            label={`Xem chú giải ${chapterNumber}`}
            onClick={() =>
              handleNoteClick(`Chú giải ${chapterNumber}`, chapterNote)
            }
          />
        )}
      </div>

      {/* Modal */}
      {activeNote && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeModal}
        >
          <div
            className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#e5e5e5] bg-[#1E40AF] px-6 py-4">
              <h3 className="text-lg font-bold text-white">
                {activeNote.title}
              </h3>
              <button
                onClick={closeModal}
                className="flex size-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <svg
                  className="size-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[60vh] overflow-y-auto p-6">
              <div
                className="prose prose-sm max-w-none whitespace-pre-wrap text-[#171717]"
                dangerouslySetInnerHTML={{ __html: activeNote.content }}
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end border-t border-[#e5e5e5] bg-[#fafafa] px-6 py-3">
              <button
                onClick={closeModal}
                className="rounded-lg bg-[#1E40AF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1E3A8A]"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function NoteLink({
  icon,
  label,
  onClick,
}: {
  icon: string
  label: string
  onClick: () => void
}) {
  const icons: Record<string, React.ReactNode> = {
    file: (
      <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
    book: (
      <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
    'book-open': (
      <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  }

  return (
    <button
      onClick={onClick}
      className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#1E40AF] transition-colors hover:text-[#1E3A8A]"
    >
      {icons[icon]}
      {label}
    </button>
  )
}
